import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useLayout } from "@/lib/layout";
import { useConsent } from "@/lib/consent";

const SLOTS = ["head", "body_start", "body_end"];

const freshScript = (old) => {
  const s = document.createElement("script");
  [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
  s.text = old.textContent;
  return s;
};

const inject = async (html, slot) => {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const anchor = slot === "body_start" ? document.body.insertBefore(document.createComment("codigo-inicio-body"), document.body.firstChild) : null;
  const place = (node) => (slot === "head" ? document.head.appendChild(node) : anchor ? document.body.insertBefore(node, anchor) : document.body.appendChild(node));
  for (const node of [...tpl.content.childNodes]) {
    if (node.nodeName !== "SCRIPT") {
      const el = place(node);
      el.querySelectorAll?.("script").forEach((inner) => inner.replaceWith(freshScript(inner)));
      continue;
    }
    const s = freshScript(node);
    const ordered = node.src && !node.async && !node.defer;
    if (ordered) s.async = false;
    const done = ordered ? new Promise((r) => { s.onload = r; s.onerror = r; }) : null;
    place(s);
    if (done) await done;
  }
};

const loadGtag = (id) => {
  if (document.getElementById("gtag-src")) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
  window.gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
  window.gtag("js", new Date());
  const s = document.createElement("script");
  s.id = "gtag-src";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
};

const loadMeta = (id) => {
  if (!window.fbq) {
    const n = function fbq() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; // eslint-disable-line prefer-rest-params
    window.fbq = n;
    window._fbq = window._fbq || n;
    Object.assign(n, { push: n, loaded: true, version: "2.0", queue: [] });
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(s);
  }
  window.fbq("init", id);
  window.fbq("track", "PageView");
};

export const Tracking = () => {
  const t = useLayout().data?.tracking;
  const consent = useConsent();
  const { pathname } = useLocation();
  const loaded = useRef({});
  const first = useRef(true);

  useEffect(() => {
    if (!t) return;
    const ok = (cat) => cat === "always" || !!consent?.[cat];
    if (Object.values(loaded.current).some((cat) => !ok(cat))) {
      window.location.reload();
      return;
    }
    const ga = t.ga4_id && ok("analytics");
    const ads = t.ads_id && ok("marketing");
    if (ga || ads) {
      loadGtag(ga ? t.ga4_id : t.ads_id);
      const m = ok("marketing") ? "granted" : "denied";
      window.gtag("consent", "update", { analytics_storage: ok("analytics") ? "granted" : "denied", ad_storage: m, ad_user_data: m, ad_personalization: m });
      if (ga && !loaded.current.ga4) { window.gtag("config", t.ga4_id); loaded.current.ga4 = "analytics"; }
      if (ads && !loaded.current.ads) { window.gtag("config", t.ads_id); loaded.current.ads = "marketing"; }
    }
    if (t.meta_pixel_id && ok("marketing") && !loaded.current.meta) { loadMeta(t.meta_pixel_id); loaded.current.meta = "marketing"; }
    SLOTS.forEach((slot) => {
      const code = t[`${slot}_code`];
      const cat = t[`${slot}_consent`];
      if (code?.trim() && ok(cat) && !loaded.current[slot]) {
        loaded.current[slot] = cat;
        inject(code, slot);
      }
    });
  }, [t, consent]);

  useEffect(() => () => {
    if (window.location.pathname.startsWith("/admin") && Object.keys(loaded.current).length) window.location.reload();
  }, []);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (loaded.current.meta) window.fbq?.("track", "PageView");
    if (loaded.current.ads) window.gtag?.("event", "page_view", { send_to: t?.ads_id });
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};
